import * as yup from 'yup';
import {
  format6Digit,
  format7ZRT,
  format8Char,
  formatDNA,
} from '../../../superadmin/components/test/useCreateManualTests';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo } from 'react';
import { useSaveDnaScannedDataMutation } from 'graphql/generated/hasura';
import { useLocation, useNavigate } from 'react-router-dom';
import { TEST_KITS_MANAGEMENT_BARCODE_SCAN_COMPLETED } from 'utilities/routes';

export const useValidateManualForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(
    () => location.state?.token || '',
    [location.state?.token],
  );
  const [saveScannedDataMutation, { loading }] = useSaveDnaScannedDataMutation({
    update: (cache, res, { variables }) => {
      navigate(TEST_KITS_MANAGEMENT_BARCODE_SCAN_COMPLETED, {
        state: {
          testKitNumber: variables?.swabBarcode ?? '',
          testKitReturnNumber: variables?.trackingNumber,
        },
        replace: true,
      });
    },
    onError: (error) => {
      if (error.message === 'Unauthorized') {
        navigate(TEST_KITS_MANAGEMENT_BARCODE_SCAN_COMPLETED, {
          state: { invalidToken: true },
          replace: true,
        });
      } else {
        navigate(TEST_KITS_MANAGEMENT_BARCODE_SCAN_COMPLETED, {
          state: { unexpectedError: true },
          replace: true,
        });
      }
    },
  });

  const validateSchema = yup.object({
    swabBarcode: yup
      .string()
      .required('Field is required')
      .test('test-kit-id-format', 'Invalid format', (value) => {
        if (!value) {
          return true;
        }
        return (
          format8Char.test(value) ||
          format6Digit.test(value) ||
          format7ZRT.test(value) ||
          formatDNA.test(value)
        );
      }),
    trackingNumber: yup.string(),
  });

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(validateSchema),
    defaultValues: {
      swabBarcode: '',
      trackingNumber: '',
    },
  });

  const handleSubmit = useCallback(
    (form: yup.InferType<typeof validateSchema>) => {
      return saveScannedDataMutation({
        variables: {
          swabBarcode: form.swabBarcode,
          trackingNumber: form?.trackingNumber
            ? form.trackingNumber
            : undefined,
          skipTrackingNumber: !form?.trackingNumber,
          token,
        },
      });
    },
    [saveScannedDataMutation, token],
  );

  return {
    methods,
    handleSubmit,
    loading,
  };
};
